const copyObject = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * The board of the Young Crew that belongs to the Member Association, as one
 * read only field of the Roles group.
 *
 * The API answers with the people holding ROLE_YC_BOARD_MEMBER in that Young
 * Crew; the Young Crew keeps its own people, a Member Association only reports
 * on them.
 */
export const YC_BOARD_MEMBER_FIELD = 'ycBoardMember';

const ycRoleFields = (boardMembers) => ({
  [YC_BOARD_MEMBER_FIELD]: {
    actualYearReport: boardMembers && boardMembers.length ? boardMembers.join(', ') : '',
  },
});

export const parseInfoReport = (report) => {
  const yearFields = {
    actualYearReport: '',
  };

  return {
    membership: {
      individualMembers: copyObject(yearFields),
      individualMembersF: copyObject(yearFields),
      individualMembersM: copyObject(yearFields),
      nationalYoungCrewMembers: copyObject(yearFields),
      nationalYoungCrewMembersF: copyObject(yearFields),
      nationalYoungCrewMembersM: copyObject(yearFields),
      corporateMembers: copyObject(yearFields),
      activeVolunteers: copyObject(yearFields),
      membershipNotes: { notes: '' },
    },
    certification: {
      certificatesNumber: { actualYearReport: report.certificatesNumber },
    },
    events: {
      eventsNumber: { actualYearReport: report.eventsNumber },
      eventsTotalParticipants: copyObject(yearFields),
      eventsNumberNonReg: copyObject(yearFields),
      eventsNotes: { notes: '' },
    },
    education: {
      regPartnersNumber: { actualYearReport: report.regPartnersNumber },
      regDiplomas: copyObject(yearFields),
      nonRegRegPartners: copyObject(yearFields),
      educationNotes: { notes: '' },
    },
    roles: {
      president: {
        actualYearReport: report.maRoles.President ? report.maRoles.President.join(', ') : '',
      },
      codDelegate: {
        actualYearReport: report.maRoles['COD Delegate']
          ? report.maRoles['COD Delegate'].join(', ')
          : '',
      },
      codSubstitute: {
        actualYearReport: report.maRoles['COD Substitute']
          ? report.maRoles['COD Substitute'].join(', ')
          : '',
      },
      headOfCb: {
        actualYearReport: report.maRoles['Head of CB']
          ? report.maRoles['Head of CB'].join(', ')
          : '',
      },
      ycChairPresident: {
        actualYearReport: report.maRoles['YC Chair/President']
          ? report.maRoles['YC Chair/President'].join(', ')
          : '',
      },
      directorOfMarketing: {
        actualYearReport: report.maRoles['Director of Marketing']
          ? report.maRoles['Director of Marketing'].join(', ')
          : '',
      },
      ...ycRoleFields(report.ycBoardMembers),
    },
  };
};

export default (report, toRead, editReport = false) => {
  const currentYear = new Date().getFullYear();
  const yearFields = {
    actualYearReport: '',
  };
  const target = {
    membership: {
      individualMembers: copyObject(yearFields),
      individualMembersF: copyObject(yearFields),
      individualMembersM: copyObject(yearFields),
      nationalYoungCrewMembers: copyObject(yearFields),
      nationalYoungCrewMembersF: copyObject(yearFields),
      nationalYoungCrewMembersM: copyObject(yearFields),
      corporateMembers: copyObject(yearFields),
      activeVolunteers: copyObject(yearFields),
      membershipNotes: { notes: '' },
    },
    events: {
      eventsTotalParticipants: copyObject(yearFields),
      eventsNumberNonReg: copyObject(yearFields),
      eventsNotes: { notes: '' },
    },
    education: {
      regDiplomas: copyObject(yearFields),
      nonRegRegPartners: copyObject(yearFields),
      educationNotes: { notes: '' },
    },
  };

  let parsed = {};

  if (toRead) {
    Object.keys(target).forEach((category) => {
      Object.keys(target[category]).forEach((field) => {
        if (!field.includes('Notes')) {
          Object.keys(yearFields).forEach((year) => {
            target[category][field][year] = report[year][field];
          });
        } else {
          target[category][field] = report[field];
        }
      });
    });

    parsed = {
      items: {
        ...target,
        certification: {
          certificatesNumber: { actualYearReport: report.certificatesNumber },
        },
        events: {
          eventsNumber: { actualYearReport: report.eventsNumber },
          ...target.events,
        },
        education: {
          regPartnersNumber: { actualYearReport: report.regPartnersNumber },
          ...target.education,
        },
        roles: {
          president: {
            actualYearReport: report.maRoles.President ? report.maRoles.President.join(', ') : '',
          },
          codDelegate: {
            actualYearReport: report.maRoles['COD Delegate']
              ? report.maRoles['COD Delegate'].join(', ')
              : '',
          },
          codSubstitute: {
            actualYearReport: report.maRoles['COD Substitute']
              ? report.maRoles['COD Substitute'].join(', ')
              : '',
          },
          headOfCb: {
            actualYearReport: report.maRoles['Head of CB']
              ? report.maRoles['Head of CB'].join(', ')
              : '',
          },
          ycChairPresident: {
            actualYearReport: report.maRoles['YC Chair/President']
              ? report.maRoles['YC Chair/President'].join(', ')
              : '',
          },
          directorOfMarketing: {
            actualYearReport: report.maRoles['Director of Marketing']
              ? report.maRoles['Director of Marketing'].join(', ')
              : '',
          },
          ...ycRoleFields(report.ycBoardMembers),
        },
      },
      actualYear: report.actualYear,
      version: report.version,
      maName: report.memberAssociation ? report.memberAssociation.name : '',
      kamNotes: report.kamNotes,
      // The accepted Young Crew report of the same year, shown in a section of
      // its own. Null when that Young Crew has none, which is the normal case
      // until the YC Management Board has decided on it.
      youngCrew: report.youngCrew || null,
    };
  } else {
    Object.keys(target).forEach((category) => {
      Object.keys(target[category]).forEach((field) => {
        if (editReport) {
          if (!field.includes('Notes')) {
            Object.keys(yearFields).forEach((year) => {
              target[category][field][year] = report[year][field] ? report[year][field] : '';
            });
          } else {
            target[category][field].notes = report[field] ? report[field] : '';
          }
        } else if (!field.includes('Notes')) {
          if (currentYear === report.actualYear) {
            target[category][field].actualYearReport = report.actualYearReport[field]
              ? report.actualYearReport[field]
              : '';
          }
        }
      });
    });
    parsed = {
      ...target,
      certification: {
        certificatesNumber: { actualYearReport: report.certificatesNumber },
      },
      events: {
        eventsNumber: { actualYearReport: report.eventsNumber },
        ...target.events,
      },
      education: {
        ...target.education,
        regPartnersNumber: { actualYearReport: report.regPartnersNumber },
      },
      roles: {
        president: {
          actualYearReport: report.maRoles.President ? report.maRoles.President.join(', ') : '',
        },
        codDelegate: {
          actualYearReport: report.maRoles['COD Delegate']
            ? report.maRoles['COD Delegate'].join(', ')
            : '',
        },
        codSubstitute: {
          actualYearReport: report.maRoles['COD Substitute']
            ? report.maRoles['COD Substitute'].join(', ')
            : '',
        },
        headOfCb: {
          actualYearReport: report.maRoles['Head of CB']
            ? report.maRoles['Head of CB'].join(', ')
            : '',
        },
        ycChairPresident: {
          actualYearReport: report.maRoles['YC Chair/President']
            ? report.maRoles['YC Chair/President'].join(', ')
            : '',
        },
        directorOfMarketing: {
          actualYearReport: report.maRoles['Director of Marketing']
            ? report.maRoles['Director of Marketing'].join(', ')
            : '',
        },
        ...ycRoleFields(report.ycBoardMembers),
      },
    };
  }

  return parsed;
};
